import { getJobsListJobsQueryKey, useJobsCancelJob, useJobsJob, useJobsListJobs, useJobsRetryJob } from "@api/generated/jobs/jobs";
import type { JobSummary } from "@api/generated/model";
import { isActiveJob, JobFileButton, JobStatusChip } from "@features/system/jobs";
import { Button, Loader, Progress, SegmentedControl } from "@mantine/core";
import { ConfirmModal } from "@shell/Dialogs";
import { useQueryClient } from "@tanstack/react-query";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { formatNumber, shortTime } from "@ui/format";
import { PageHeader } from "@ui/PageHeader";
import { Splitter } from "@ui/Splitter";
import { type Column, DataTable, useDataTable } from "@ui/table/DataTable";
import type { TFunction } from "i18next";
import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { z } from "zod";

const search = z.object({ everyone: z.boolean().optional(), job: z.string().optional() });
export const Route = createFileRoute("/system/jobs")({ validateSearch: search, component: Jobs });

const LIMIT = 200;

const resultText = (j: JobSummary) => j.message ?? (typeof j.result?.summary === "string" ? j.result.summary : "");

function duration(j: JobSummary, t: TFunction): string | undefined {
  if (!j.started_at || !j.finished_at) return undefined;
  const s = Math.max(0, (new Date(j.finished_at).getTime() - new Date(j.started_at).getTime()) / 1000);
  return s < 90 ? t("jobs.seconds", { s: formatNumber(s, s < 10 ? 1 : 0) }) : t("jobs.minutes", { m: Math.round(s / 60) });
}

function Jobs() {
  const { t } = useTranslation();
  const params = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });
  const list = useJobsListJobs(
    { everyone: params.everyone ?? false, limit: LIMIT },
    { query: { refetchInterval: (q) => (q.state.data?.some(isActiveJob) ? 2000 : 15000) } },
  );
  const jobs = list.data ?? [];
  const selected = params.job ?? jobs.find(isActiveJob)?.id ?? jobs[0]?.id;
  const columns = useMemo<Column<JobSummary>[]>(
    () => [
      {
        id: "job",
        header: t("jobs.job"),
        value: (j) => j.title,
        className: "wrap",
        cell: (j) => <b style={{ fontWeight: 600 }}>{j.title}</b>,
      },
      { id: "started_by", header: t("jobs.startedBy"), value: (j) => j.started_by },
      {
        id: "started",
        header: t("jobs.started"),
        value: (j) => j.created_at,
        text: (j) => shortTime(j.created_at),
        className: "mono",
      },
      {
        id: "status",
        header: t("jobs.status"),
        value: (j) => t(`jobs.statuses.${j.status}`),
        cell: (j) =>
          j.status === "running" ? (
            <span className="hstack" style={{ gap: 8 }}>
              <Progress value={j.progress} size="sm" w={120} />
              {j.progress} %
            </span>
          ) : (
            <JobStatusChip status={j.status} extra={j.status === "done" ? duration(j, t) : undefined} />
          ),
      },
      {
        id: "result",
        header: t("jobs.result"),
        value: (j) => (j.file_name && j.status === "done" ? j.file_name : resultText(j)),
        className: "wrap sm",
        cell: (j) => (
          <>
            <JobFileButton job={j} label={j.file_name ?? undefined} />
            {!(j.file_name && j.status === "done") && <span className="mu">{resultText(j)}</span>}
          </>
        ),
      },
    ],
    [t],
  );
  // the server lists the newest LIMIT jobs: sorting and filtering apply to those
  const table = useDataTable("system.jobs", columns, list.data, { partial: jobs.length >= LIMIT });

  return (
    <div className="page fill">
      <PageHeader
        title={t("jobs.title")}
        subtitle={t("jobs.subtitle")}
        actions={
          <SegmentedControl
            color="breeder"
            value={params.everyone ? "all" : "own"}
            onChange={(v) => navigate({ search: (s) => ({ ...s, everyone: v === "all" ? true : undefined, job: undefined }) })}
            data={[
              { value: "own", label: t("jobs.mine") },
              { value: "all", label: t("jobs.everyone") },
            ]}
          />
        }
      />
      <div className="fill-row">
        <section className="card grow stack" style={{ minHeight: 0 }}>
          {list.isPending ? (
            <Loader size="sm" m="md" />
          ) : jobs.length === 0 ? (
            <p className="mu sm" style={{ padding: 16 }}>
              {t("jobs.empty")}
            </p>
          ) : (
            <DataTable
              table={table}
              rowKey={(j) => j.id}
              className="grow"
              rowProps={(j) => ({
                className: `click${j.id === selected ? " sel" : ""}`,
                onClick: () => navigate({ search: (s) => ({ ...s, job: j.id }) }),
              })}
            />
          )}
        </section>
        {selected && <Splitter cssVar="--job-panel-w" side="end" min={280} max={720} />}
        {selected && <JobPanel id={selected} />}
      </div>
    </div>
  );
}

function JobPanel({ id }: { id: string }) {
  const { t } = useTranslation();
  const client = useQueryClient();
  const detail = useJobsJob(id, { query: { refetchInterval: (q) => (q.state.data && isActiveJob(q.state.data.job) ? 2000 : false) } });
  const logEnd = useRef<HTMLDivElement>(null);
  const refresh = () => {
    void client.invalidateQueries({ queryKey: getJobsListJobsQueryKey() });
    void detail.refetch();
  };
  const [cancelling, setCancelling] = useState(false);
  const cancel = useJobsCancelJob({ mutation: { onSuccess: refresh } });
  const retry = useJobsRetryJob({ mutation: { onSuccess: refresh } });
  const lines = detail.data?.log.length ?? 0;
  // block body: scrollIntoView returns a Promise in current browsers, and an effect must not return a non-function
  // biome-ignore lint/correctness/useExhaustiveDependencies: scroll when a new log line arrives
  useEffect(() => {
    logEnd.current?.scrollIntoView({ block: "end" });
  }, [lines]);

  if (!detail.data) return <aside className="card" style={{ width: "var(--job-panel-w, 360px)", flexShrink: 0 }} />;
  const { job, log } = detail.data;
  return (
    <aside
      className="card"
      style={{ width: "var(--job-panel-w, 360px)", flexShrink: 0, display: "flex", flexDirection: "column", overflow: "hidden" }}
    >
      <div className="stack" style={{ padding: "14px 16px", gap: 6, borderBottom: "1px solid var(--b-border)" }}>
        <div className="hstack" style={{ gap: 8 }}>
          <h2 className="h2 grow">{job.title}</h2>
          <JobStatusChip status={job.status} />
        </div>
        <Progress value={job.progress} size="sm" />
        {job.message && <span className="sm mu">{job.message}</span>}
      </div>
      <div
        role="log"
        aria-label={t("jobs.log")}
        style={{
          flexGrow: log.length ? 1 : 0,
          background: "var(--b-console)",
          padding: "12px 14px",
          overflowY: "auto",
          fontFamily: "var(--b-mono)",
          fontSize: "var(--b-text-small)",
          lineHeight: 1.7,
          color: "var(--b-console-ink)",
        }}
      >
        {log.length === 0 && <div style={{ opacity: 0.6 }}>{t("jobs.emptyLog")}</div>}
        {log.map((l, i) => (
          <div
            // biome-ignore lint/suspicious/noArrayIndexKey: log lines are append-only and have no id
            key={i}
            style={{ color: l.level === "error" ? "#f0a39a" : l.level === "warning" ? "#e8c777" : undefined, whiteSpace: "pre-wrap" }}
          >
            {shortTime(l.ts, true)} {l.text}
          </div>
        ))}
        <div ref={logEnd} />
      </div>
      <div className="hstack" style={{ padding: "var(--b-inset-padding)", gap: 8, borderTop: "1px solid var(--b-border)" }}>
        {isActiveJob(job) && (
          <Button variant="default" c="var(--b-red)" loading={cancel.isPending} onClick={() => setCancelling(true)}>
            {t("jobs.cancel")}
          </Button>
        )}
        {(job.status === "failed" || job.status === "cancelled") && (
          <Button variant="default" loading={retry.isPending} onClick={() => retry.mutate({ jobId: job.id })}>
            {t("jobs.retry")}
          </Button>
        )}
        <div style={{ flexGrow: 1 }} />
        <JobFileButton job={job} size="sm" />
      </div>
      {cancelling && isActiveJob(job) && (
        <ConfirmModal
          title={t("jobs.cancel")}
          text={t("jobs.cancelConfirm", { title: job.title, percent: job.progress })}
          confirm={t("jobs.cancel")}
          danger
          loading={cancel.isPending}
          onConfirm={() => cancel.mutate({ jobId: job.id }, { onSettled: () => setCancelling(false) })}
          onClose={() => setCancelling(false)}
        />
      )}
    </aside>
  );
}
